import { z } from 'zod';
import { TimestampSchema } from '../primitives.js';
/**
 * Call metadata shared by the call record and the session lifecycle record
 * (doc 33 W2/W7/W9). Its own module so `call.ts` and `session.ts` can both
 * depend on it without importing each other.
 */
export const SessionTransport = z.enum(['twilio', 'telnyx', 'browser']);
/** The conversation engine a call ran on (doc 47). Kept apart from the
 * transport: a Twilio call can run either engine. */
export const VoiceEngineKind = z.enum(['realtime', 'live']);
/**
 * Which engine and backend a call ran under (doc 47 §3). Null on every
 * Realtime call written before the GPT-Live experiment existed, which is why
 * it is defaulted rather than required.
 */
export const EngineMetaSchema = z.object({
    kind: VoiceEngineKind,
    /** The voice model (`gpt-live-1`), or the Realtime model. */
    voiceModel: z.string().max(100),
    /** The delegated Responses model (`gpt-6-luna`); null for Realtime. */
    backendModel: z.string().max(100).nullable().default(null),
    delegation: z.enum(['responses', 'none']).default('none'),
    reasoningEffort: z.string().max(20).nullable().default(null),
    serviceTier: z.string().max(40).nullable().default(null),
    /** Live splits the prompt in two; each half is versioned. */
    voicePromptVersion: z.string().max(40).nullable().default(null),
    backendPromptVersion: z.string().max(40).nullable().default(null),
    /** How a booking change is confirmed: `tts_readback` (application-owned
     * clip of known text) or `blocked` (no mutations at all). */
    confirmationMode: z.string().max(40).nullable().default(null),
    /** How cancellations and moves are authorised: `direct` (doc 71) — the
     * caller's clear request commits both, missing facts are asked as typed
     * clarifications, never a confirmation question; `adaptive` (doc 66 W4) —
     * cancellations only, moves read back; `always` — the earlier
     * read-back-first policy; `none` — no gate, the assistant's tool call
     * commits (ownership and slot still checked). Null on records written
     * before it existed. */
    cancellationConfirmation: z.enum(['direct', 'adaptive', 'always', 'none']).nullable().default(null),
    /** Who spoke normal turns on a Live call (doc 68): `mixed` (gateway
     * greeting/progress/line check), `native` (the Live voice; gateway speech
     * disabled; confirmation audio is independently checked before playback).
     * Null for Realtime and older records. */
    speechDelivery: z.enum(['mixed', 'native']).nullable().default(null),
    /** Immutable trial label: engine + prompts + voice + run. */
    experimentId: z.string().max(80).nullable().default(null),
});
/** What the conversation ran under — enough to reproduce it (doc 33 W9 #6). */
export const CallConfigMetaSchema = z.object({
    model: z.string().max(100),
    transcribeModel: z.string().max(100).default(''),
    voice: z.string().max(40).default(''),
    promptVersion: z.string().max(40),
    toolSchemaVersion: z.string().max(40),
    /** Canonical hash of the business configuration snapshot the call used. */
    businessConfigVersion: z.string().max(80),
    /** Hash of the whole effective configuration (platform + business). */
    configHash: z.string().max(80),
    /** Cloud Run revision (`K_REVISION`). */
    deploymentRevision: z.string().max(120).default(''),
    /** Deploy-profile content hash (`CONFIG_PROFILE_VERSION`). */
    profileVersion: z.string().max(80).default(''),
    transport: SessionTransport,
    /** A labelled experiment (e.g. a different browser model), never silent. */
    experiment: z.string().max(80).nullable().default(null),
    /** The engine (doc 47). Absent/null = Realtime, as every older record was. */
    engine: EngineMetaSchema.nullable().default(null),
});
const count = () => z.number().int().min(0).default(0);
/**
 * Model usage for one call, by billable category (doc 47 §8). Buckets are
 * DISJOINT: cached and cache-write tokens are not also counted as plain
 * input, and reasoning tokens are a breakdown of output, never added to it.
 * `complete: false` means at least one final usage report never arrived —
 * the cost is then a lower bound, never a measured zero.
 */
export const EngineUsageSchema = z.object({
    pricingVersion: z.string().max(40),
    engine: VoiceEngineKind,
    complete: z.boolean(),
    /** Which reports were missing (e.g. `live_final_usage`). */
    missing: z.array(z.string().max(60)).max(10).default([]),
    /** Live: final cumulative active seconds (replaced, never summed). */
    liveSeconds: z.number().min(0).nullable().default(null),
    backend: z
        .object({
        responses: count(),
        incomplete: count(),
        failed: count(),
        inputTokens: count(),
        cachedInputTokens: count(),
        cacheWriteTokens: count(),
        outputTokens: count(),
        /** Part of outputTokens; reported, never added again. */
        reasoningTokens: count(),
    })
        .nullable()
        .default(null),
    realtime: z
        .object({
        responses: count(),
        textInputTokens: count(),
        cachedTextInputTokens: count(),
        audioInputTokens: count(),
        cachedAudioInputTokens: count(),
        textOutputTokens: count(),
        audioOutputTokens: count(),
    })
        .nullable()
        .default(null),
    transcription: z
        .object({ requests: count(), audioInputTokens: count(), textInputTokens: count(), textOutputTokens: count(), seconds: z.number().min(0).default(0) })
        .nullable()
        .default(null),
    /** Gateway-owned speech (read-backs, fallback, hold phrases), metered when
     * GENERATED, played or not (doc 48 F6). The speech endpoint returns no
     * usage, so its cost is always an estimate. Cache hits cost nothing. */
    tts: z
        .object({
        requests: count(),
        characters: count(),
        audioSeconds: z.number().min(0).default(0),
        cacheHits: count(),
        failures: count(),
        /** Failed after producing audio: how much was billed is unknown. */
        partialFailures: count(),
    })
        .nullable()
        .default(null),
    /** The models and tier the cost was computed for (doc 48 F8). */
    pricedAs: z
        .object({
        voiceModel: z.string().max(100),
        backendModel: z.string().max(100).nullable().default(null),
        serviceTier: z.string().max(40).nullable().default(null),
        transcribeModel: z.string().max(100).nullable().default(null),
        ttsModel: z.string().max(100).nullable().default(null),
    })
        .nullable()
        .default(null),
    costUsd: z
        .object({
        /** Priced components only — a LOWER BOUND when `lowerBound` is true. */
        total: z.number().min(0),
        components: z.record(z.string(), z.number().min(0)),
        /** True when any component is estimated or a report was missing. */
        estimated: z.boolean(),
        /** The part of `total` that is an estimate (gateway speech). */
        estimatedUsd: z.number().min(0).default(0),
        /** Usage was missing or a model was unpriced: the true cost is higher. */
        lowerBound: z.boolean().default(false),
        /** Models/tiers with no verified price in this version — never guessed. */
        unpriced: z.array(z.string().max(100)).max(10).default([]),
    })
        .nullable()
        .default(null),
});
export const FinalizationSource = z.enum(['gateway', 'carrier_callback', 'browser', 'reconciler']);
/**
 * The completion marker on a call record (doc 33 W7 #9). Consumers treat a
 * record as final when `final` is true; `version` only ever grows, so a
 * provisional carrier record can be superseded by the gateway's, and an
 * out-of-order write can never overwrite a newer one.
 *
 * Versions: carrier callback 1 (provisional, `final: false`), gateway/browser
 * 10, reconciler 5 — a reconciler run never overrides the gateway's own record.
 * A correction by the same source increments within its decade.
 */
export const CallFinalizationSchema = z.object({
    version: z.number().int().min(0),
    final: z.boolean(),
    source: FinalizationSource,
    at: TimestampSchema.optional(),
});
export const FINALIZATION_VERSION = {
    carrier_callback: 1,
    reconciler: 5,
    gateway: 10,
    browser: 10,
};
//# sourceMappingURL=callMeta.js.map